import React, { useState, useEffect, useRef } from 'react';
import { renderToStaticMarkup } from 'react-dom/server';
import map from 'lodash/map';

import { chartColors, defaultBoostThreshold } from 'helpers/constants';
import { ChartNoDataText } from 'sharedComponents/ChartComponents';
import { translate } from 'helpers/translateHelper';
import colors from 'theme/_colors.scss';
import { chartLegendItemClick } from '../../../../helpers';
import FFTTooltip from './Tooltip/FFTTooltip';
import { getMeasurementUnitSymbol } from 'helpers/unitHelper';
import { chartHeightConstants, PiezoSensor, MAX_PRECISION } from '../constants';
import { formatDateTime, normalizeUtcTimestamp } from 'helpers/dateTimeFormatter';
import { bearingFrequencyTypes, showBearingFaultHarmonics, SPEED_LABEL, SPEED_UNIT } from './constants';
import { setMaxPrecision } from 'helpers/formatters';

const chartConfig = {
    title: {
        text: null
    },
    chart: {
        zoomType: 'x',
        events: {},
        spacingTop: 20, // Reserve room above the plot so highest-peak flags aren't clipped
    },
    colors: chartColors,
    boost: {
        allowForce: false //Workaround for highchart bug. Allows correctly disabling boost, when zooming in.
    },
    time: {
        useUTC: false
    },
    xAxis: [
        {
            id: 'x1',
            crosshair: {
                enabled: true,
                width: 2,
                color: colors.tundora,
            }
        },
        {
            id: 'x2',
        }
    ],
    yAxis: {
        min: 0,
        events: {
            afterSetExtremes: function (e) {
                // After every extremes change (including zoom), force y-axis to start at 0
                if (e.min > 0) {
                    this.setExtremes(0, e.max, true, false);
                }
            }
        }
    },
    legend: {
        align: 'right',
        enabled: true,
        symbolRadius: 0,
        squareSymbol: false,
        symbolHeight: 8,
        symbolWidth: 8
    },
    credits: {
        enabled: false,
    },
    tooltip: {
        shared: true,
        valueDecimals: 2,
        useHTML: true,
        backgroundColor: 'rgba(255, 255, 255, 0.6)',
        shadow: false,
        borderWidth: 0,
        dateTimeLabelFormats: {
            day: '%d.%m.%Y<br/>%H:%M',
            hour: '%d.%m %H:%M',
        },
    },
    plotOptions: {
        series: {
            turboThreshold: 0,
            animation: false,
            boostThreshold: 0,
        }
    },
    lang: {
        noData: 'No data'
    },
    noData: {
        useHTML: true
    },
    exporting: {
        enabled: false,
        buttons: {
            contextButton: {
                menuItems: ['downloadPNG'],
                verticalAlign: 'bottom',
                x: 10,
                y: 0
            }
        }
    }
};

const getPeaksFromSelectedSensors = (fftsByTimestampData, visibleAxes, spectrumRangeSelection) => {
    if (!fftsByTimestampData?.sensors || !visibleAxes?.length) {
        return [];
    }
    const isPiezo = spectrumRangeSelection === PiezoSensor.id;
    const getAxis = (n) => n === PiezoSensor.axisName ? translate('ABB.Powertrain.Frontend.highFrequencyRadialLabel') : n;
    return fftsByTimestampData.sensors
        .flatMap(s => s.dataValues || [])
        .filter(d => isPiezo === (d.sensorAxisName === PiezoSensor.axisName) &&
            visibleAxes.includes(getAxis(d.sensorAxisName)) && d.harmonicsHighestPeaks?.length)
        .flatMap(d => d.harmonicsHighestPeaks.map(p => ({ ...p, axis: getAxis(d.sensorAxisName) })))
        .sort((a, b) => b.magnitude - a.magnitude)
        .slice(0, 5)
        .map((p, i) => ({ ...p, order: i + 1 }));
};

const getAllPossibleAxes = (fftsByTimestampData) => {
    return [...new Set(
        fftsByTimestampData?.sensors?.flatMap(sensor =>
            sensor.dataValues?.map(dv => dv.sensorAxisName === PiezoSensor.axisName
                ? translate('ABB.Powertrain.Frontend.highFrequencyRadialLabel')
                : dv.sensorAxisName) || []
        ) || []
    )].filter(axis => axis !== translate('ABB.Powertrain.Frontend.highFrequencyRadialLabel'));
};

const getVisibleAxesForHighestPeaks = (fftsByTimestampData, chartSeries, selected2DLegends) => {
    const hasExplicitSelections = selected2DLegends && Object.keys(selected2DLegends).length > 0;
    const allPossibleAxes = getAllPossibleAxes(fftsByTimestampData);

    if (chartSeries?.length) {
        const allSeriesNames = chartSeries
            .filter(s => !(s.options?.custom?.isTimestamp || s.custom?.isTimestamp))
            .map(s => s.name || s.options?.name)
            .filter(Boolean);

        if (hasExplicitSelections && allSeriesNames.length > 0) {
            return allSeriesNames.filter(name => selected2DLegends[name] !== false);
        }

        const visibleFromChart = chartSeries
            .filter(s => !(s.options?.custom?.isTimestamp || s.custom?.isTimestamp) && (s.visible ?? true))
            .map(s => s.name || s.options?.name)
            .filter(Boolean);

        if (visibleFromChart.length > 0) {
            return visibleFromChart;
        }
    }

    if (hasExplicitSelections && allPossibleAxes.length > 0) {
        return allPossibleAxes.filter(axis => selected2DLegends[axis] !== false);
    }

    return allPossibleAxes;
};

const getPeaksToDisplayForHighestPeaks = (harmonicsHighestPeaks, fftsByTimestampData, visibleAxes, selectedSpectrumRange) => {
    if (!fftsByTimestampData || !visibleAxes?.length) {
        return harmonicsHighestPeaks?.slice(0, 5) || [];
    }

    const standardAxes = [...new Set(visibleAxes)].filter(axis =>
        axis !== translate('ABB.Powertrain.Frontend.highFrequencyRadialLabel') && axis !== 'Highest Peaks'
    );

    const allPossibleAxes = getAllPossibleAxes(fftsByTimestampData);

    if (standardAxes.length === allPossibleAxes?.length && harmonicsHighestPeaks?.length) {
        return harmonicsHighestPeaks.slice(0, 5).map((peak) => ({ ...peak, order: peak.order, outsideSensors: true }));
    }

    const peaksFromSensors = getPeaksFromSelectedSensors(fftsByTimestampData, standardAxes, selectedSpectrumRange) || [];
    return peaksFromSensors.slice(0, 5).map((peak, i) => ({ ...peak, order: i + 1, outsideSensors: false }));
};

export const getTableHighestPeaks = (harmonicsHighestPeaks, fftsByTimestampData, selectedSpectrumRange, selected2DLegends) => {
    const visibleAxes = getVisibleAxesForHighestPeaks(fftsByTimestampData, null, selected2DLegends || {});
    return getPeaksToDisplayForHighestPeaks(harmonicsHighestPeaks, fftsByTimestampData, visibleAxes, selectedSpectrumRange);
};

const createxAxisPlotlines = (
    bearingFaultFrequencies,
    bearingFaultSwitch,
    bearingFrequencies,
    bearingFaultType,
    selectedSpectrumRange,
    harmonics,
    speedHarmonicsSwitch,
    isRPM,
    hasBearingFaultFrequencies,
    hasHarmonics
) => {
    const plotLines = [];

    if (showBearingFaultHarmonics.includes(selectedSpectrumRange)) {
        let BSFCount = 0, BPFICount = 0, BPFOCount = 0;

        const getDashStyle = (frequencyName) => {
            if (frequencyName?.includes(bearingFrequencyTypes.BSF.name)) {
                return [bearingFrequencyTypes.BSF, ++BSFCount];
            } else if (frequencyName?.includes(bearingFrequencyTypes.BPFI.name)) {
                return [bearingFrequencyTypes.BPFI, ++BPFICount];
            } else if (frequencyName?.includes(bearingFrequencyTypes.BPFO.name)) {
                return [bearingFrequencyTypes.BPFO, ++BPFOCount];
            }
        };

        if (hasBearingFaultFrequencies && bearingFaultSwitch) {
            bearingFaultFrequencies?.map((frequency) => {
                if (frequency?.bearingType?.includes(bearingFaultType)) {
                    const [bearingType, count] = getDashStyle(frequency?.name);
                    if (bearingFrequencies[bearingType?.name]) {
                        plotLines.push(
                            {
                                dashStyle: bearingType.style,
                                color: colors.black,
                                width: 1,
                                value: !isRPM ? frequency?.value : frequency?.value * 60,
                                zIndex: 1,
                                id: frequency?.name,
                                label: {
                                    text: count,
                                    align: 'center',
                                    rotation: 0,
                                    y: 0,
                                    style: {
                                        color: colors.black,
                                    }
                                }
                            }
                        );
                    }
                }
            });
        }

        if (hasHarmonics && speedHarmonicsSwitch) {
            harmonics?.map((harmonic, index) => {
                plotLines.push(
                    {
                        dashStyle: 'Solid',
                        color: colors.alto,
                        width: 1,
                        value: !isRPM ? harmonic?.value : harmonic?.value * 60,
                        zIndex: 1,
                        id: harmonic?.name,
                        label: {
                            text: index + 1,
                            align: 'center',
                            rotation: 0,
                            y: 0,
                            style: {
                                color: 'rgba(0, 0, 0, 0.64)'
                            }
                        }
                    }
                );
            });
        }

        return plotLines;
    }

    return plotLines;
};

// Create flags series for highest peaks
const createHighestPeaksFlagsSeries = (
    harmonicsHighestPeaks,
    hasSpeed,
    speedHarmonicsSwitch,
    selectedSpectrumRange,
    fftsByTimestampData,
    chartSeries,
    selected2DLegends
) => {
    if (!speedHarmonicsSwitch || !showBearingFaultHarmonics.includes(selectedSpectrumRange)) {
        return null;
    }

    const visibleAxes = getVisibleAxesForHighestPeaks(fftsByTimestampData, chartSeries, selected2DLegends || {});
    const peaksToDisplay = getPeaksToDisplayForHighestPeaks(harmonicsHighestPeaks, fftsByTimestampData, visibleAxes, selectedSpectrumRange);

    if (peaksToDisplay?.length > 0) {
        const flagsData = peaksToDisplay?.map(peak => ({
            x: peak.frequencyValue,
            y: peak.magnitude,
            title: peak.order.toString(),
            outsideSensors: peak.outsideSensors
        }));
        return {
            type: 'flags',
            name: 'Highest Peaks',
            data: flagsData,
            shape: 'circlepin',
            width: 12,
            height: 12,
            y: -30,
            clip: false, // Prevent flags near the top of the plot area from being clipped
            showInLegend: false,
            color: colors.white,
            fillColor: colors.white,
            lineWidth: 1,
            lineColor: colors.black,
            accessibility: {
                exposeAsGroupOnly: true,
            },
            style: {
                color: colors.black,
                fontSize: '10px',
                fontWeight: 400,
                fontFamily: 'ABBvoice-regular, sans-serif',
            },
            custom: {
                isHighestPeaks: true,
            },
        };
    }

    return null;
};

const getFormattedData = (fftData, spectrumRangeSelection, signalVisibility) => {

    const {
        dataValues: data,
        magnitudeUnitSymbol,
        frequencyUnitSymbol
    } = fftData || {};

    // Added Series data for showing Timestamp legend
    const formattedData = [{
        name: `${formatDateTime(normalizeUtcTimestamp(fftData?.timestamp))} — ${SPEED_LABEL}: ${fftData?.speed !== null && fftData?.speed !== undefined ? setMaxPrecision(Number(fftData.speed), MAX_PRECISION) + ' ' + (fftData?.speedUnitSymbol || SPEED_UNIT) : '—'}`,
        showInlegend: true,
        enableMouseTracking: false,
        custom: {
            isTimestamp: true,
        },
        data: [],
        color: 'transparent',
        marker: {
            enabled: false
        }
    }];
    let seriesLength = 0;

    if (data) {
        data.forEach((item, index) => {
            if (spectrumRangeSelection === PiezoSensor.id && item.sensorAxisName === PiezoSensor.axisName
                || spectrumRangeSelection !== PiezoSensor.id && item.sensorAxisName !== PiezoSensor.axisName) {
                const seriesData = map(item.sensorAxisDataValues, (dataValues) => { return [dataValues.frequency, dataValues.magnitude]; });
                seriesLength += seriesData.length;

                const seriesName = item.sensorAxisName === PiezoSensor.axisName
                    ? translate('ABB.Powertrain.Frontend.highFrequencyRadialLabel')
                    : item.sensorAxisName;
                formattedData.push({
                    name: seriesName,
                    data: seriesData,
                    lineWidth: 1,
                    id: index,
                    visible: signalVisibility[seriesName] !== undefined ? signalVisibility[seriesName] : true,
                    tooltip: {
                        headerFormat: '',
                        pointFormat: ` <b>${frequencyUnitSymbol}:</b> {point.x} <br /><b>${magnitudeUnitSymbol}:</b> {point.y}`
                    },
                    connectNulls: true,
                    color: item.sensorAxisName === PiezoSensor.axisName ? chartColors[0] : chartColors[index]
                });
            }
        });
    }
    return { formattedData, seriesLength };
};


const use2DChartConfig = (
    fft2DData,
    selected2DLegends,
    selectedSpectrumRange,
    setSelectedLegends,
    containerRef,
    bearingFaultSwitch,
    bearingFrequencies,
    bearingFaultType,
    speedHarmonicsSwitch
) => {
    const [options, setOptions] = useState({});
    const selectedLegendsRef = useRef(selected2DLegends);
    selectedLegendsRef.current = selected2DLegends;
    const fftData = fft2DData?.[0] || {};

    useEffect(() => {
        const containerHeight = containerRef?.current?.clientHeight || chartHeightConstants.default;
        chartConfig.chart.height = Math.max(chartHeightConstants.min, containerHeight - chartHeightConstants.reserved);
    }, []);

    useEffect(() => {
        const { formattedData, seriesLength } = getFormattedData(fftData, selectedSpectrumRange, selected2DLegends) || {};
        chartConfig.series = formattedData;
        chartConfig.plotOptions.series.boostThreshold = seriesLength > defaultBoostThreshold ? 1 : 0;
        chartConfig.xAxis[0].title = { text: `${translate('ABB.Powertrain.Frontend.FFTxAxisText')} [${fftData?.frequencyUnitSymbol}]` };
        chartConfig.xAxis[1].title = { text: `${translate('ABB.Powertrain.Frontend.opFFTRevolutionsPerMinuteText')} [${getMeasurementUnitSymbol(14).toUpperCase()}]` };
        chartConfig.yAxis.title = { text: `${translate('ABB.Powertrain.Frontend.FFTyAxisText')} [${fftData?.magnitudeUnitSymbol}]` };
        chartConfig.lang.noData = renderToStaticMarkup(
            <ChartNoDataText
                title={translate('ABB.Powertrain.Frontend.chartNoMeasurementData')}
                text={translate('ABB.Powertrain.Frontend.chartNoMeasurementDataUserGuide')} />);
        chartConfig.enableNoDataZoom = true;
        chartConfig.tooltip.formatter = function () {
            if (this.points?.length) {
                return renderToStaticMarkup(<FFTTooltip points={this.points}
                    frequencyUnitSymbol={fftData?.frequencyUnitSymbol}
                    magnitudeUnitSymbol={fftData?.magnitudeUnitSymbol}
                    precision={fftData?.precision}
                    timeStamp={this.x} />);
            }
        };
        chartConfig.xAxis[0].plotLines = createxAxisPlotlines(
            fftData?.bearingFaultFrequencies,
            bearingFaultSwitch,
            bearingFrequencies,
            bearingFaultType,
            selectedSpectrumRange,
            fftData?.harmonics,
            speedHarmonicsSwitch,
            false,
            fftData?.hasBearingFaultFrequencies,
            fftData?.hasHarmonics
        );
        chartConfig.xAxis[1].plotLines = createxAxisPlotlines(
            fftData?.bearingFaultFrequencies,
            bearingFaultSwitch,
            bearingFrequencies,
            bearingFaultType,
            selectedSpectrumRange,
            fftData?.harmonics,
            speedHarmonicsSwitch,
            true,
            fftData?.hasBearingFaultFrequencies,
            fftData?.hasHarmonics
        );
        // Add flags series for highest peaks
        const highestPeaksFlags = createHighestPeaksFlagsSeries(
            fftData?.harmonicsHighestPeaks,
            fftData?.hasSpeed,
            speedHarmonicsSwitch,
            selectedSpectrumRange,
            fftData,
            chartConfig.series,
            selected2DLegends
        );
        if (highestPeaksFlags) {
            chartConfig.series = chartConfig.series?.filter(series => series.type !== 'flags');
            chartConfig.series = [...chartConfig.series, highestPeaksFlags];
        } else {
            chartConfig.series = chartConfig.series?.filter(series => series.type !== 'flags');
        }

        chartConfig.plotOptions.series.events = {
            legendItemClick: function (e) {
                if (this?.options?.custom?.isTimestamp) {
                    return false;
                }
                // Count visible signals (excluding timestamp legends)
                const visibleSignals = this?.chart?.series?.filter(
                    signal => !signal.options?.custom?.isTimestamp &&
                        !signal.options?.custom?.isHighestPeaks &&
                        signal.visible
                );
                if (
                    visibleSignals.length > 1 ||
                    visibleSignals.length === 1 && visibleSignals[0]?.options?.name !== this?.options?.name
                ) {
                    setSelectedLegends({
                        ...selectedLegendsRef?.current || {},
                        [this.options.name]: !this.visible
                    });
                }
                chartLegendItemClick(e);
            }
        };
        setOptions({ ...chartConfig });
    }, [
        fft2DData,
        selectedSpectrumRange,
        selected2DLegends
    ]);

    useEffect(() => {
        chartConfig.xAxis[0].plotLines = createxAxisPlotlines(
            fftData?.bearingFaultFrequencies,
            bearingFaultSwitch,
            bearingFrequencies,
            bearingFaultType,
            selectedSpectrumRange,
            fftData?.harmonics,
            speedHarmonicsSwitch,
            false,
            fftData?.hasBearingFaultFrequencies,
            fftData?.hasHarmonics
        );
        chartConfig.xAxis[1].plotLines = createxAxisPlotlines(
            fftData?.bearingFaultFrequencies,
            bearingFaultSwitch,
            bearingFrequencies,
            bearingFaultType,
            selectedSpectrumRange,
            fftData?.harmonics,
            speedHarmonicsSwitch,
            true,
            fftData?.hasBearingFaultFrequencies,
            fftData?.hasHarmonics
        );
        // Add flags series for highest peaks
        const highestPeaksFlags = createHighestPeaksFlagsSeries(
            fftData?.harmonicsHighestPeaks,
            fftData?.hasSpeed,
            speedHarmonicsSwitch,
            selectedSpectrumRange,
            fftData,
            chartConfig.series,
            selected2DLegends
        );
        if (highestPeaksFlags) {
            chartConfig.series = chartConfig.series?.filter(series => series.type !== 'flags');
            chartConfig.series = [...chartConfig.series, highestPeaksFlags];
        } else {
            chartConfig.series = chartConfig.series?.filter(series => series.type !== 'flags');
        }
        setOptions({ ...chartConfig });
    }, [
        bearingFaultSwitch,
        bearingFrequencies,
        bearingFaultType,
        speedHarmonicsSwitch,
    ]);

    return options;
};

export default use2DChartConfig;
